import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';

import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { FirebaseAppProvider } from 'reactfire';

import { GlobalStyles, ThemeProvider, createTheme } from '@mui/material';

import firebase_config from './firebase_api/firebase_config';

import 'styles/styles.css';

import LoadingSpinner from './components/loading_spinners/LoadingSpinner';

import { gradients, palette } from 'styles/palette';

import { LicenseInfo } from '@mui/x-license';
import type {} from '@mui/x-data-grid-pro/themeAugmentation';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { shouldRetryQuery } from 'components/api_adapters/shouldRetryQuery';
import { mockDatabaseData } from 'tests/API_mocks/i3d';
import { env_config } from 'env_config';
import { isRecaptchaDisabled } from 'runtime_env';

const FirestoreProvider = lazy(() => import('./firebase_api/FirebaseProvider')
	.then((module) => ({ default: module.FirestoreProvider })));
const AuthDataProvider   = lazy(() => import('app/providers/AuthDataProvider'));
const ScrollManager      = lazy(() => import('app/ScrollWrapper'));
const AppContent         = lazy(() => import('./app/AppContent'));

declare module '@mui/material/Button' {
	interface ButtonPropsVariantOverrides {
		pu_primary: true;
		ghost_outlined: true;
		pu_secondary: true;
		cancel: true;
		success: true;
	}
}

declare module '@mui/material/styles' {
	interface Theme {
		gradients: {
			primaryHeader: string;
		};
	}
	interface ThemeOptions {
		gradients?: {
			primaryHeader?: string;
		};
	}
	interface Palette {
		slate: Partial<Record<number, string>>;
		navy: Partial<Record<number, string>>;
		teal: Partial<Record<number, string>>;
	}
	interface PaletteOptions {
		slate?: Partial<Record<number, string>>;
		navy?: Partial<Record<number, string>>;
		teal?: Partial<Record<number, string>>;
	}
}

const theme = createTheme({
	spacing: 8,
	gradients: gradients,
	typography: {
		fontFamily: 'Roboto, Arial, sans-serif',
		fontSize: 12,
		h1: {
			fontSize: '2rem',
			fontWeight: 500,
			m: 0
		},
		h2: {
			fontSize: '1.5rem',
			fontWeight: 500,
			m: 0
		},
		h3: {
			fontSize: '1.17rem',
			color: '#1b6378',
			fontWeight: 500,
			m: 0
		},
		h4: {
			fontSize: '1rem',
			color: '#1b6378',
			fontWeight: 500,
			m: 0
		},
		h5: {
			fontSize: '0.83rem',
			fontWeight: 500,
			m: 0
		},
		h6: {
			fontSize: '0.67rem',
			fontWeight: 500,
			m: 0
		},
		body1: {
			fontSize: '.83rem',
			fontWeight: 400
		},
		body2: {
			fontSize: '0.83rem',
			color: 'black',
			fontWeight: 400,
			whiteSpace: 'pre-wrap'
		},
		caption: {
			fontSize: '12px',
			fontWeight: 400,
			color: palette.slate[500]
		}
	},
	palette: palette,
	components: {
		MuiDataGrid: {
			styleOverrides: {
				root: {
					'& .MuiDataGrid-scrollShadow': {
						display: 'none'
					}
				}
			}
		},
		// v9 reduced ListItemIcon's default minWidth from 56px to 36px (theme.spacing(4.5)),
		// which shrank the gap next to avatars/icons in lists. Restore the pre-v9 spacing.
		MuiListItemIcon: {
			styleOverrides: {
				root: {
					minWidth: 56
				}
			}
		},
		MuiButton: {
			styleOverrides: {
				root: {
					textTransform: 'none',
					variants: [
						{
							props: { variant: 'pu_primary', color: 'primary' },
							style: {
								display: 'flex',
								textTransform: 'none',
								alignItems: 'center',
								padding: '6px',
								fontSize: '0.75rem',
								fontWeight: 500,
								clipPath: 'polygon(2px 0%, calc(100% - 2px) 0%, 100% 2px, 100% calc(100% - 8px), calc(100% - 8px) 100%, 2px 100%, 0% calc(100% - 2px), 0% 2px)',
								height: '40px',
								minWidth: '100px',
								backgroundColor: palette.navy[700],
								color: '#fff',
								'&:hover': {
									backgroundColor: palette.navy[900],
									color: '#fff'
								},
								'&:disabled': {
									backgroundColor: palette.slate[500],
									color: '#fff'
								}
							}
						},
						{
							props: { variant: 'ghost_outlined', color: 'primary' },
							style: {
								display: 'flex',
								textTransform: 'none',
								alignItems: 'center',
								padding: '6px',
								fontSize: '0.75rem',
								fontWeight: 500,
								background: 'linear-gradient(135deg, transparent 4px, #CAD5DA 5px, #CAD5DA 7px, transparent 7.5px) right -1px bottom -1px / 8px 8px no-repeat',
								clipPath: 'polygon(2px 0%, calc(100% - 2px) 0%, 100% 2px, 100% calc(100% - 8px), calc(100% - 8px) 100%, 2px 100%, 0% calc(100% - 2px), 0% 2px)',
								height: '40px',
								minWidth: '100px',
								backgroundColor: 'transparent',
								border: '1px solid #CAD5DA',
								color: '#313D42',
								transition: 'color 0.2s ease-in-out 0.15s',
								'&:hover': {
									borderColor: '#576C75',
									color: '#0E333E',
									background: 'linear-gradient(135deg, transparent 4px, #576C75 5px, #576C75 7px, transparent 7.5px) right -1px bottom -1px / 8px 8px no-repeat'

								},
								'&:disabled': {
									borderColor: '#CAD5DA',
									color: '#8FA3AC',
									background: 'linear-gradient(135deg, transparent 5px, #CAD5DA 5px, #CAD5DA 7px, transparent 7.5px) right -1px bottom -1px / 8px 8px no-repeat'

								}
							}
						},
						{
							props: { variant: 'pu_secondary', color: 'primary' },
							style: {
								backgroundColor: 'transparent',
								textTransform: 'none',
								color: '#1b6378',
								fontSize: '0.75rem',
								alignItems: 'flex-end',
								'&:hover': {
									backgroundColor: 'transparent',
									color: 'rgba(27, 99, 120, 0.5)'
								}
							}
						},
						{
							props: { variant: 'cancel', color: 'primary' },
							style: {
								backgroundColor: 'transparent',
								textTransform: 'none',
								color: 'rgb(178, 34, 34)',
								fontSize: '0.75rem',
								height: '40px',
								'&:hover': {
									backgroundColor: 'transparent',
									color: 'rgba(178, 34, 34, 0.5)'
								}
							}
						},
						{
							props: { variant: 'success', color: 'primary' },
							style: {
								backgroundColor: 'transparent',
								textTransform: 'none',
								color: '#46af84',
								fontSize: '0.75rem',
								height: '40px',
								'&:hover': {
									backgroundColor: 'transparent',
									color: '#71c6ac'
								}
							}
						},
						{
							props: { variant: 'text', color: 'primary' },
							style: {
								backgroundColor: 'transparent',
								textTransform: 'none',
								color:  palette.navy[700],
								'&:hover': {
									color: palette.navy[900]
								}
							}
						}

					]

				}
			}
		}
	}
});

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: shouldRetryQuery
		}
	}
});

// Wraps everything in an app provider, which makes the Firebase app available everywehre
const App = () => {

	LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_KEY ?? 'Key Not Available');

	// Generic error handler for ResizeObserver errors that keep popping up from ResizeObservers
	useEffect(() => {
		// Generic error handler for ResizeObserver errors thrown by Timeline
		const handleObserverError = (e: ErrorEvent) => {
			if (e.message === 'ResizeObserver loop limit exceeded' || e.message === 'Script error.' || e.message === 'ResizeObserver loop completed with undelivered notifications.') {
				const resizeObserverErrDiv = document.getElementById(
					'webpack-dev-server-client-overlay-div'
				);
				const resizeObserverErr = document.getElementById(
					'webpack-dev-server-client-overlay'
				);
				if (resizeObserverErr) {
					resizeObserverErr.setAttribute('style', 'display: none');
				}
				if (resizeObserverErrDiv) {
					resizeObserverErrDiv.setAttribute('style', 'display: none');
				}
			}
		};

		// Add observer error handler to window object
		window.addEventListener('error', (e) => handleObserverError(e));

		// Cleanup observers on unmount
		return () => window.removeEventListener('error', (e) => handleObserverError(e));
	}, []);

	if (process.env.REACT_APP_TEST === 'true') {mockDatabaseData();}

	LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_KEY ?? 'Key Not Available');

	const app_tree = (
		<QueryClientProvider client={queryClient}>
			<ReactQueryDevtools initialIsOpen={false} />
			<Suspense
				fallback={<LoadingSpinner />}
			>
				<FirestoreProvider>
					<AuthDataProvider>
						<BrowserRouter >
							<ScrollManager />
							<AppContent />
						</BrowserRouter >
					</AuthDataProvider>
				</FirestoreProvider>
			</Suspense>
		</QueryClientProvider>
	);

	return (
		<ThemeProvider theme={theme}>
			<GlobalStyles styles={{
				h1: {
					margin: 0,
					fontWeight: 500
				},
				h2: {
					margin: 0
				},
				h3: {
					margin: 0
				},
				h4: {
					margin: 0
				},
				h5: {
					margin: 0
				},
				h6: {
					margin: 0
				},
				body: {
					justifyContent: 'center',
					margin: 0,
					height: '100vh',
					display: 'flex',
					alignItems: 'center'
				},
				ul: {
					listStyle: 'none',
					padding: 0,
					margin: 0
				},
				html: {
					height: '100%',
					overflow: 'auto',
					boxSizing: 'border-box',
					fontFamily: 'Roboto, Arial, sans-serif'
				}
			}}
			/>
			<FirebaseAppProvider
				firebaseConfig={firebase_config()}
				suspense={true}
			>

				{isRecaptchaDisabled()
					? app_tree
					: (
						<GoogleReCaptchaProvider reCaptchaKey={env_config().recaptcha_site_key}
							useEnterprise={true}
							scriptProps={{
								defer: true
							}}
						>
							{app_tree}
						</GoogleReCaptchaProvider>
					)}
			</FirebaseAppProvider>
		</ThemeProvider>
	);
};

export default App;
